/**
 * CertificateUploader — upload/estado do certificado digital A1 (e-CNPJ .pfx/.p12),
 * reutilizável no modal da empresa e no gerador de NFS-e. Envia via a Edge Function
 * `nfse-upload-certificate` (que valida + custodia cifrado; a senha nunca é exposta).
 * Extraído do antigo StepCertificate do FiscalOnboardingWizard.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Lock, ShieldCheck, FileCheck2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { certExpiryStatus } from '@/lib/certExpiry';
import type { FiscalCertificate } from '@/types/fiscal';

const CERT_ERROR_MESSAGES: Record<string, string> = {
  invalid_pkcs12: 'Não foi possível abrir o certificado. Verifique o arquivo e a senha.',
  wrong_password: 'Senha do certificado incorreta.',
  cnpj_mismatch: 'O CNPJ do certificado não confere com o CNPJ informado na empresa.',
  expired: 'Este certificado está vencido.',
  not_yet_valid: 'Este certificado ainda não é válido.',
  forbidden: 'Somente o proprietário da empresa pode enviar o certificado.',
  profile_missing: 'Complete os dados fiscais (CNPJ e município) antes de enviar o certificado.',
  consent_required: 'É necessário autorizar a custódia do certificado (LGPD).',
};

async function fileToBase64(file: File): Promise<string> {
  const buf = new Uint8Array(await file.arrayBuffer());
  let bin = '';
  const chunk = 0x8000;
  for (let i = 0; i < buf.length; i += chunk) bin += String.fromCharCode(...buf.subarray(i, i + chunk));
  return btoa(bin);
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
}

export function CertificateUploader({
  companyId,
  isOwner,
  onUploaded,
}: {
  companyId: string | null;
  isOwner: boolean;
  onUploaded?: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState('');
  const [consent, setConsent] = useState(false);
  const [sending, setSending] = useState(false);

  const { data: cert, refetch } = useQuery({
    queryKey: ['fiscal-active-cert', companyId],
    enabled: Boolean(companyId),
    queryFn: async (): Promise<FiscalCertificate | null> => {
      const { data, error } = await supabase
        .from('fiscal_certificates')
        .select('*')
        .eq('company_id', companyId as string)
        .eq('status', 'active')
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
  });

  if (!companyId) {
    return (
      <p className="rounded-md border border-dashed bg-muted/40 p-3 text-xs text-muted-foreground">
        Salve a empresa primeiro para enviar o certificado digital.
      </p>
    );
  }
  if (!isOwner) {
    return (
      <p className="flex items-center gap-2 rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
        <Lock className="h-4 w-4 shrink-0" />
        Somente o proprietário da empresa pode enviar o certificado A1.
      </p>
    );
  }

  const submit = async () => {
    if (!file) return;
    setSending(true);
    try {
      const pfx_base64 = await fileToBase64(file);
      const { error } = await supabase.functions.invoke('nfse-upload-certificate', {
        body: { company_id: companyId, pfx_base64, password, consent: true },
      });
      if (error) {
        let code = 'erro';
        try {
          const ctx = (error as { context?: Response }).context;
          if (ctx && typeof ctx.json === 'function') code = (await ctx.json())?.code ?? code;
        } catch { /* genérico */ }
        toast.error(CERT_ERROR_MESSAGES[code] ?? 'Não foi possível enviar o certificado.');
        return;
      }
      toast.success('Certificado enviado e validado com sucesso!');
      setFile(null);
      setPassword('');
      setConsent(false);
      await refetch();
      onUploaded?.();
    } catch {
      toast.error('Não foi possível enviar o certificado.');
    } finally {
      setSending(false);
    }
  };

  const expiry = certExpiryStatus(cert?.not_after ?? null);

  return (
    <div className="space-y-3">
      {cert && (
        <div
          className={
            'flex items-center gap-2 rounded-md border p-2.5 text-xs ' +
            (expiry.level === 'expired'
              ? 'border-red-300 bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300'
              : expiry.level === 'urgent' || expiry.level === 'warn'
                ? 'border-amber-300 bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'
                : 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300')
          }
        >
          {expiry.level === 'expired' || expiry.level === 'urgent' || expiry.level === 'warn'
            ? <AlertTriangle className="h-4 w-4 shrink-0" />
            : <CheckCircle2 className="h-4 w-4 shrink-0" />}
          <span>
            {expiry.level === 'expired'
              ? `Certificado vencido em ${fmtDate(cert.not_after)} — envie um novo.`
              : `Certificado ativo, válido até ${fmtDate(cert.not_after)}.`}
          </span>
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="pfx">{cert ? 'Substituir certificado (.pfx / .p12)' : 'Arquivo do certificado (.pfx / .p12)'}</Label>
        <Input
          id="pfx"
          type="file"
          accept=".pfx,.p12,application/x-pkcs12"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
      </div>

      {file && (
        <>
          <div className="space-y-1.5">
            <Label htmlFor="pfx-pass">Senha do certificado</Label>
            <Input id="pfx-pass" type="password" autoComplete="off" value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <Label htmlFor="cert-consent" className="flex cursor-pointer items-start gap-3 rounded-lg border p-3">
            <Checkbox id="cert-consent" checked={consent} onCheckedChange={(v) => setConsent(v === true)} className="mt-0.5" />
            <span className="text-xs text-muted-foreground">
              Autorizo o Orçamigo a custodiar meu certificado digital de forma cifrada para emitir NFS-e em nome da minha empresa (LGPD).
            </span>
          </Label>
          <Button3D type="button" className="w-full" disabled={!file || !consent || sending} onClick={submit}>
            {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ShieldCheck className="mr-2 h-4 w-4" />}
            Enviar certificado
          </Button3D>
          <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground">
            <FileCheck2 className="h-3.5 w-3.5" />
            Validamos que o CNPJ do certificado é o mesmo da empresa.
          </p>
        </>
      )}
    </div>
  );
}
